import { useState, useEffect, useRef } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { X, Send, ChevronRight, MessageSquare, Wand2 } from 'lucide-react'
import { LoadingSpinner } from '../ui/LoadingSpinner'
import { ImageAnnotationTool } from './ImageAnnotationTool'
import type { GeneratedImage, AutoMaskMode } from '../../lib/api'
import { getImageAssetUrl, editVisualImage, generateAutoMask, ApiError } from '../../lib/api'
import { toast } from 'sonner'

interface EditSessionPanelProps {
  isOpen: boolean
  onClose: () => void
  image: GeneratedImage | null
  /** All images for the current task, used to build the edit chain timeline */
  allImages: GeneratedImage[]
  contentId?: string | null
  visualTaskId?: string | null
  onSuccess?: () => void
}

/** Walk source_image_id backwards to build the chain from root to given image */
function buildEditChain(image: GeneratedImage, allImages: GeneratedImage[]): GeneratedImage[] {
  const chain: GeneratedImage[] = [image]
  let current = image
  while (current.source_image_id) {
    const parent = allImages.find((img) => img.id === current.source_image_id)
    if (!parent) break
    chain.unshift(parent)
    current = parent
  }
  return chain
}

export function EditSessionPanel({
  isOpen,
  onClose,
  image,
  allImages,
  contentId,
  visualTaskId,
  onSuccess,
}: EditSessionPanelProps) {
  const [instruction, setInstruction] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [useMask, setUseMask] = useState(false)
  const [maskDataUrl, setMaskDataUrl] = useState<string | null>(null)
  const [selectedChainImage, setSelectedChainImage] = useState<GeneratedImage | null>(null)
  const [autoMaskMode, setAutoMaskMode] = useState<AutoMaskMode>('foreground')
  const [isLoadingAutoMask, setIsLoadingAutoMask] = useState(false)
  const [pendingMaskDataUrl, setPendingMaskDataUrl] = useState<string | null>(null)

  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const chain = image ? buildEditChain(image, allImages) : []
  const activeImage = selectedChainImage ?? image

  useEffect(() => {
    if (isOpen && image) {
      setSelectedChainImage(null)
      setInstruction('')
      setMaskDataUrl(null)
      setUseMask(false)
      setPendingMaskDataUrl(null)
    }
  }, [isOpen, image])

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => textareaRef.current?.focus(), 300)
    }
  }, [isOpen])

  const handleAutoMask = async () => {
    if (!activeImage?.id) return
    setIsLoadingAutoMask(true)
    try {
      const result = await generateAutoMask(activeImage.id, autoMaskMode)
      setPendingMaskDataUrl(result.mask_data_url)
      setUseMask(true)
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error('Error al generar máscara automática', { description: err.message })
      } else {
        toast.error('Error inesperado', {
          description: err instanceof Error ? err.message : 'No se pudo generar la máscara',
        })
      }
    } finally {
      setIsLoadingAutoMask(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = instruction.trim()
    if (!trimmed || !activeImage) return
    setIsSubmitting(true)
    try {
      await editVisualImage({
        source_image_id: activeImage.id,
        instruction: trimmed,
        content_id: contentId ?? null,
        visual_task_id: visualTaskId ?? null,
        ...(useMask && maskDataUrl ? { mask_data_url: maskDataUrl } : {}),
      })
      toast.success('Edición enviada', {
        description: 'La nueva imagen aparecerá en la galería cuando esté lista.',
      })
      setInstruction('')
      setMaskDataUrl(null)
      setUseMask(false)
      onSuccess?.()
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error('Error al editar la imagen', { description: err.message })
      } else {
        toast.error('Error inesperado', {
          description: err instanceof Error ? err.message : 'No se pudo enviar la edición',
        })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const imageUrl = activeImage
    ? activeImage.id
      ? getImageAssetUrl(activeImage.id)
      : (activeImage.public_url ?? '')
    : ''

  const imageFallbackUrl = activeImage?.public_url ?? null

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            className="fixed inset-0 bg-black/40 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Side panel */}
          <motion.div
            key="panel"
            className="fixed top-0 right-0 h-full w-full max-w-xl bg-white shadow-2xl z-50 flex flex-col"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 flex-shrink-0">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-slate-500" />
                <h2 className="text-lg font-semibold text-slate-800">Sesión de edición</h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-500 hover:text-slate-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Edit chain timeline */}
            {chain.length > 0 && (
              <div className="px-5 py-3 border-b border-slate-100 flex-shrink-0 bg-slate-50">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                  Historial de turnos
                </p>
                <div className="flex items-center gap-1 overflow-x-auto pb-1">
                  {chain.map((img, idx) => {
                    const isActive = activeImage?.id === img.id
                    const thumbUrl = img.id ? getImageAssetUrl(img.id) : (img.public_url ?? '')
                    return (
                      <div key={img.id} className="flex items-center gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => setSelectedChainImage(img)}
                          className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                            isActive
                              ? 'border-emerald-500 shadow-md scale-105'
                              : 'border-slate-200 hover:border-slate-400 opacity-70 hover:opacity-100'
                          }`}
                          title={`Turno ${idx + 1}`}
                        >
                          <div className="w-14 h-14 bg-slate-200">
                            {thumbUrl && (
                              <img
                                src={thumbUrl}
                                alt={`Turno ${idx + 1}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  if (img.public_url) {
                                    ;(e.target as HTMLImageElement).src = img.public_url
                                  }
                                }}
                              />
                            )}
                          </div>
                          <span className="absolute bottom-0.5 left-0.5 text-[10px] font-bold bg-black/60 text-white px-1 rounded">
                            {idx + 1}
                          </span>
                        </button>
                        {idx < chain.length - 1 && (
                          <ChevronRight className="w-3 h-3 text-slate-400 flex-shrink-0" />
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Image preview + annotation */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {imageUrl ? (
                useMask ? (
                  <ImageAnnotationTool
                    imageUrl={imageUrl}
                    imageUrlFallback={imageFallbackUrl}
                    onMaskChange={setMaskDataUrl}
                    initialMaskDataUrl={pendingMaskDataUrl}
                    className="w-full"
                  />
                ) : (
                  <div className="rounded-xl overflow-hidden border border-slate-200 bg-slate-50 flex items-center justify-center min-h-[200px]">
                    <img
                      src={imageUrl}
                      alt="Imagen a editar"
                      className="max-w-full max-h-[45vh] object-contain"
                      onError={(e) => {
                        if (imageFallbackUrl) {
                          ;(e.target as HTMLImageElement).src = imageFallbackUrl
                        }
                      }}
                    />
                  </div>
                )
              ) : (
                <div className="rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-center h-40">
                  <p className="text-slate-400 text-sm">Imagen no disponible</p>
                </div>
              )}

              {/* Mask controls */}
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useMask}
                    onChange={(e) => {
                      setUseMask(e.target.checked)
                      if (!e.target.checked) {
                        setMaskDataUrl(null)
                        setPendingMaskDataUrl(null)
                      }
                    }}
                    className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-slate-600">Usar máscara (marcar área a editar)</span>
                </label>

                {/* Auto-mask section */}
                <div className="flex items-center gap-2 ml-auto">
                  <select
                    value={autoMaskMode}
                    onChange={(e) => setAutoMaskMode(e.target.value as AutoMaskMode)}
                    className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    title="Modo de detección automática"
                  >
                    <option value="foreground">Primer plano</option>
                    <option value="background">Fondo</option>
                    <option value="semantic">Semántico</option>
                  </select>
                  <button
                    type="button"
                    onClick={handleAutoMask}
                    disabled={isLoadingAutoMask || !activeImage?.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Detectar máscara automáticamente con IA"
                  >
                    {isLoadingAutoMask ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <Wand2 className="w-4 h-4" />
                    )}
                    Detectar automáticamente
                  </button>
                </div>
              </div>
            </div>

            {/* Edit form — sticky at bottom */}
            <form
              onSubmit={handleSubmit}
              className="flex-shrink-0 px-5 py-4 border-t border-slate-200 bg-white"
            >
              <label htmlFor="edit-session-instruction" className="block text-sm font-medium text-slate-700 mb-2">
                Instrucción de edición
              </label>
              <div className="flex gap-2 items-end">
                <textarea
                  id="edit-session-instruction"
                  ref={textareaRef}
                  className="input flex-1 min-h-[72px] resize-none"
                  rows={3}
                  placeholder="Describe los cambios que quieres aplicar..."
                  value={instruction}
                  onChange={(e) => setInstruction(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault()
                      if (instruction.trim() && !isSubmitting) {
                        handleSubmit(e as unknown as React.FormEvent)
                      }
                    }
                  }}
                  required
                />
                <button
                  type="submit"
                  disabled={!instruction.trim() || isSubmitting}
                  className="btn btn-primary flex items-center gap-2 self-end mb-0.5"
                >
                  {isSubmitting ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Editar
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-1.5">Ctrl+Enter para enviar</p>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
